import React from 'react'
import { Alert, Flex } from 'antd'
import { useTranslation } from 'react-i18next'
import { PeriodQuery } from '../dashboard'
import { useAdminSpend } from '../hooks'
import { MetricTile } from './metric-tile'
import { formatMicros, SpendTables } from './spend-tables'

const compact = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
})

export function AdminSpend(query: PeriodQuery) {
    const { data: spend, isPending, isError } = useAdminSpend(query)
    const { t } = useTranslation()

    if (isError) {
        return (
            <Alert
                type="error"
                showIcon
                message={t('Could not load statistics for this period.')}
            />
        )
    }

    const usdToEur = spend?.usdToEur ?? 0
    // OpenAI bills in dollars and Meta in euros; the total converts dollars with the rate in config/spend.php.
    const openaiMicrodollars = (spend?.openai.costMicrodollars ?? 0) + (spend?.openai.storageMicrodollars ?? 0)
    const whatsappMicros = spend?.whatsapp.evaaMicros ?? 0
    const totalEurMicros = openaiMicrodollars * usdToEur + whatsappMicros
    const whatsappCurrency = spend?.whatsappCurrency ?? 'EUR'

    const perVisitorMessage = spend?.perVisitorMessage
    const perConversation = spend?.perConversation

    return (
        <Flex
            vertical
            gap="large"
        >
            {/* Cost — the story. Recorded spend leads; OpenAI and WhatsApp are line items with
                their own basis folded in, so the counts support the figure instead of competing
                as heroes of their own. */}
            <div className="rounded-xl border border-hairline bg-white p-5 md:p-6">
                <Flex className="flex-col gap-6 md:flex-row md:items-stretch md:justify-between">
                    <Flex
                        vertical
                        gap={2}
                    >
                        <span className="text-xs font-medium uppercase tracking-wide text-ink-tertiary">
                            {t('Spend')}
                        </span>
                        {isPending ? (
                            <div className="mt-1 h-9 w-32 animate-pulse rounded bg-hairline" />
                        ) : (
                            <span className="text-3xl font-semibold tabular-nums leading-tight text-ink">
                                {formatMicros(totalEurMicros, 'EUR')}
                            </span>
                        )}
                        <span className="text-xs text-ink-tertiary">
                            {t('this period, dollars at {{rate}} euro', { rate: usdToEur })}
                        </span>
                    </Flex>

                    <div className="hidden w-px self-stretch bg-hairline md:block" />

                    <Flex
                        vertical
                        gap={12}
                        className="md:min-w-[320px]"
                    >
                        <CostLine
                            label={t('OpenAI')}
                            basis={t('{{calls}} calls · {{searches}} searches · storage {{storage}}', {
                                calls: compact.format(spend?.openai.calls ?? 0),
                                searches: compact.format(spend?.openai.searches ?? 0),
                                storage: formatMicros(spend?.openai.storageMicrodollars ?? 0, 'USD'),
                            })}
                            amount={formatMicros(openaiMicrodollars, 'USD')}
                            loading={isPending}
                        />
                        <CostLine
                            label={t('WhatsApp (Evaa numbers)')}
                            basis={t('{{delivered}} delivered · Meta reports {{meta}} · customers pay {{customer}}', {
                                delivered: compact.format(spend?.whatsapp.delivered ?? 0),
                                meta: formatMicros(spend?.whatsapp.metaEvaaMicros ?? 0, whatsappCurrency),
                                customer: formatMicros(spend?.whatsapp.customerMicros ?? 0, whatsappCurrency),
                            })}
                            amount={formatMicros(whatsappMicros, whatsappCurrency)}
                            loading={isPending}
                        />
                        {(spend?.openai.unpricedCalls ?? 0) + (spend?.whatsapp.unpriced ?? 0) > 0 && (
                            <span className="text-xs text-ink-tertiary">
                                {t(
                                    '{{count}} items without a price (before recording, or a model or category missing in config/spend.php)',
                                    {
                                        count: (spend?.openai.unpricedCalls ?? 0) + (spend?.whatsapp.unpriced ?? 0),
                                    }
                                )}
                            </span>
                        )}
                    </Flex>
                </Flex>
            </div>

            <div className="overflow-hidden rounded-xl border border-hairline">
                <div className="grid grid-cols-1 gap-px bg-hairline md:grid-cols-2">
                    <MetricTile
                        loading={isPending}
                        label={t('Cost per visitor message')}
                        value={
                            perVisitorMessage && perVisitorMessage.visitorMessages > 0
                                ? formatMicros(
                                      perVisitorMessage.costMicrodollars / perVisitorMessage.visitorMessages,
                                      'USD'
                                  )
                                : undefined
                        }
                        hint={t('{{count}} visitor messages, model and searches', {
                            count: perVisitorMessage?.visitorMessages ?? 0,
                        })}
                    />
                    <MetricTile
                        loading={isPending}
                        label={t('Cost per conversation')}
                        value={
                            perConversation && perConversation.conversations > 0
                                ? formatMicros(
                                      (perConversation.aiMicrodollars * usdToEur + perConversation.whatsappMicros) /
                                          perConversation.conversations,
                                      'EUR'
                                  )
                                : undefined
                        }
                        hint={t('{{count}} conversations on Evaa numbers: agent {{ai}}, WhatsApp {{whatsapp}}', {
                            count: perConversation?.conversations ?? 0,
                            ai: formatMicros(
                                perConversation && perConversation.conversations > 0
                                    ? perConversation.aiMicrodollars / perConversation.conversations
                                    : 0,
                                'USD'
                            ),
                            whatsapp: formatMicros(
                                perConversation && perConversation.conversations > 0
                                    ? perConversation.whatsappMicros / perConversation.conversations
                                    : 0,
                                whatsappCurrency
                            ),
                        })}
                    />
                </div>
            </div>

            <SpendTables
                spend={spend}
                loading={isPending}
            />
        </Flex>
    )
}

type CostLineProps = {
    label: React.ReactNode
    basis: React.ReactNode
    amount: React.ReactNode
    loading?: boolean
}

function CostLine({ label, basis, amount, loading }: CostLineProps) {
    return (
        <Flex
            justify="space-between"
            align="start"
            gap="middle"
        >
            <Flex
                vertical
                gap={1}
            >
                <span className="text-ink">{label}</span>
                <span className="text-xs tabular-nums text-ink-tertiary">{basis}</span>
            </Flex>
            {loading ? (
                <div className="mt-1 h-5 w-16 animate-pulse rounded bg-hairline" />
            ) : (
                <span className="tabular-nums font-medium text-ink">{amount}</span>
            )}
        </Flex>
    )
}
